import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Card, CardContent, Typography, Chip, Alert, CircularProgress,
  Grid, IconButton, Tooltip, Tabs, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Switch
} from '@mui/material';
import {
  Refresh, Schedule, CloudSync, CheckCircle, Error as ErrorIcon,
  Warning as WarningIcon, Cloud, Storage, AccessTime, PlayCircle,
  Radio as RadioIcon, Home as HomeIcon, Backup as BackupIcon
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import { GlassmorphicCard, CardTitle, BodyText, SecondaryText } from '../styles/sharedStyles';

const StyledCard = styled(GlassmorphicCard)(({ theme }) => ({
  height: '100%',
  '&:hover': {
    transform: 'translateY(-5px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.02)',
  borderRadius: '16px',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  '& .MuiTableHead-root': {
    '& .MuiTableCell-head': {
      background: 'rgba(255, 255, 255, 0.05)',
      color: '#ffffff',
      fontWeight: 600,
      fontSize: '0.875rem',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    },
  },
  '& .MuiTableBody-root': {
    '& .MuiTableRow-root': {
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.02)',
      },
      '& .MuiTableCell-body': {
        color: '#ffffff',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        fontSize: '0.875rem',
      },
    },
  },
}));

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'status'
})(({ status }) => {
  let bgColor, textColor, borderColor;

  const statusStr = String(status || 'unknown').toLowerCase();

  switch (statusStr) {
    case 'running':
    case 'active':
    case 'enabled':
    case 'true':
      bgColor = 'rgba(0, 255, 136, 0.1)';
      textColor = '#00ff88';
      borderColor = 'rgba(0, 255, 136, 0.3)';
      break;
    case 'stopped':
    case 'down':
    case 'disabled':
    case 'false':
      bgColor = 'rgba(255, 68, 68, 0.1)';
      textColor = '#ff4444';
      borderColor = 'rgba(255, 68, 68, 0.3)';
      break;
    case 'pending':
    case 'starting':
    case 'stopping':
      bgColor = 'rgba(255, 193, 7, 0.1)';
      textColor = '#ffc107';
      borderColor = 'rgba(255, 193, 7, 0.3)';
      break;
    default:
      bgColor = 'rgba(158, 158, 158, 0.1)';
      textColor = '#9e9e9e';
      borderColor = 'rgba(158, 158, 158, 0.3)';
  }

  return {
    background: bgColor,
    color: textColor,
    border: `1px solid ${borderColor}`,
    fontWeight: 500,
  };
});

const DataProtection = ({ apiBaseUrl, token, onlyTrueNAS = false, onlyRpi = false }) => {
  const [activeTab, setActiveTab] = useState(onlyRpi ? 1 : 0);
  const [snapshotTasks, setSnapshotTasks] = useState([]);
  const [cloudSyncTasks, setCloudSyncTasks] = useState([]);
  const [rpiBackupScheduled, setRpiBackupScheduled] = useState(null);
  const [rpiBackupList, setRpiBackupList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDataProtection = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const requests = [];
      
      if (!onlyRpi) {
        requests.push(
          axios.get(`${apiBaseUrl}/api/data-protection/snapshots`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${apiBaseUrl}/api/data-protection/cloud-sync`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        );
      } else {
        // Add null placeholders to keep indices consistent if needed, 
        // but it's better to just handle the responses based on what was requested
      }

      if (!onlyTrueNAS) {
        requests.push(
          axios.get(`${apiBaseUrl}/api/backup/scheduled`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${apiBaseUrl}/api/backup/list`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        );
      }

      const results = await Promise.all(requests);
      let resIdx = 0;

      if (!onlyRpi) {
        setSnapshotTasks(results[resIdx++].data.tasks || []);
        setCloudSyncTasks(results[resIdx++].data.tasks || []);
      }
      
      if (!onlyTrueNAS) {
        setRpiBackupScheduled(results[resIdx++].data || null);
        setRpiBackupList(results[resIdx++].data?.backups || []);
      }
    } catch (err) {
      console.error('Error fetching data protection tasks:', err);
      setError(err.response?.data?.detail || 'Failed to fetch data protection tasks');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token, onlyTrueNAS, onlyRpi]);

  const runTask = async (type, id) => {
    try {
      const response = await axios.post(
        `${apiBaseUrl}/api/truenas/tasks/${type}/${id}/run`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        // Show success (you might want a Toast instead of alert)
        // For now using alert to be consistent with other parts of the app if they use it
        // but checking if there's a better way
        alert(`✅ ${response.data.message}`);
        // Refresh after a short delay to see the "Running" status
        setTimeout(fetchDataProtection, 2000);
      } else {
        alert(`⚠️ ${response.data.message}`);
      }
    } catch (err) {
      console.error(`Error running ${type} task:`, err);
      alert(`❌ Failed to run task: ${err.response?.data?.detail || err.message}`);
    }
  };

  useEffect(() => {
    fetchDataProtection();
    // Auto-refresh every 5 minutes (300000 ms) to avoid rate limiting
    const interval = setInterval(fetchDataProtection, 300000);
    return () => clearInterval(interval);
  }, [fetchDataProtection]);

  const formatSchedule = (schedule) => {
    if (!schedule) return 'Not scheduled';
    const parts = [];
    if (schedule.minute !== undefined && schedule.minute !== '*') parts.push(`Min: ${schedule.minute}`);
    if (schedule.hour !== undefined && schedule.hour !== '*') parts.push(`Hour: ${schedule.hour}`);
    if (schedule.dom !== undefined && schedule.dom !== '*') parts.push(`Day: ${schedule.dom}`);
    if (schedule.month !== undefined && schedule.month !== '*') parts.push(`Month: ${schedule.month}`);
    if (schedule.dow !== undefined && schedule.dow !== '*') parts.push(`DOW: ${schedule.dow}`);
    return parts.length > 0 ? parts.join(', ') : 'Custom schedule';
  };

  const formatRetention = (lifetimeValue, lifetimeUnit) => {
    if (!lifetimeValue || !lifetimeUnit) return 'No retention';
    return `${lifetimeValue} ${lifetimeUnit}${lifetimeValue > 1 ? 's' : ''}`;
  };

  const formatBytes = (bytes) => {
    if (bytes === 0 || bytes === undefined || bytes === null) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getSnapshotStatus = (task) => {
    if (task.enabled === false) return 'disabled';
    return 'active';
  };

  const getCloudSyncStatus = (task) => {
    if (task.enabled === false) return 'disabled';
    if (task.job?.state === 'RUNNING') return 'running';
    if (task.job?.state === 'SUCCESS') return 'active';
    if (task.job?.state === 'FAILED') return 'error';
    return 'pending';
  };

  if (loading && snapshotTasks.length === 0 && cloudSyncTasks.length === 0 && !rpiBackupScheduled) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#00d4ff' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
          Data Protection
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchDataProtection} disabled={loading} sx={{ color: '#00d4ff' }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
          {error}
        </Alert>
      )}

      {!onlyTrueNAS && !onlyRpi && (
        <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(event, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: { xs: '40px', sm: '48px' },
              '& .MuiTabs-indicator': {
                background: (theme) => theme.palette.primary.main,
                height: '3px'
              },
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.6)',
                textTransform: 'none',
                fontSize: { xs: '0.72rem', sm: '0.85rem' },
                fontWeight: 500,
                py: { xs: 1, sm: 1.5 },
                px: { xs: 1.5, sm: 2.5 },
                minWidth: { xs: 'auto', sm: '110px' },
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: '#ffffff',
                  background: 'rgba(255, 255, 255, 0.05)'
                },
                '&.Mui-selected': {
                  color: (theme) => theme.palette.primary.main,
                  background: (theme) => alpha(theme.palette.primary.main, 0.05)
                }
              }
            }}
          >
            <Tab icon={<img src="/truenas_logo.png" alt="TrueNAS" style={{ width: 20, height: 20 }} />} iconPosition="start" label="TrueNAS" />
            <Tab icon={<img src="/raspberry-pi.png" alt="RPi" style={{ width: 20, height: 20 }} />} iconPosition="start" label="RPi Backup" />
          </Tabs>
        </Box>
      )}

      {/* TrueNAS Content - Contains Snapshot Tasks and Cloud Sync Tasks */}
      {(!onlyRpi && (activeTab === 0 || onlyTrueNAS)) && (
        <Box>
          {/* Snapshot Tasks Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Schedule sx={{ color: 'rgba(0, 212, 255, 0.85)' }} />
              Snapshot Tasks
            </Typography>
            {snapshotTasks.length === 0 ? (
              <Box sx={{
                textAlign: 'center',
                py: 4,
                color: 'rgba(255, 255, 255, 0.7)'
              }}>
                <Typography variant="body1">
                  {loading ? 'Loading snapshot tasks...' : 'No snapshot tasks found'}
                </Typography>
              </Box>
            ) : (
              <StyledTableContainer component={Paper}>
                <Table>
                  <TableHead>
                      <TableRow>
                        <TableCell>Dataset</TableCell>
                        <TableCell>Schedule</TableCell>
                        <TableCell>Retention</TableCell>
                        <TableCell>Recursive</TableCell>
                        <TableCell>State</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {snapshotTasks.map((task) => (
                        <TableRow key={task.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Storage sx={{ color: '#00d4ff', fontSize: 20 }} />
                              <Typography variant="body2" sx={{ color: '#ffffff' }}>
                                {task.dataset}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AccessTime sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 18 }} />
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                                {formatSchedule(task.schedule)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                              {formatRetention(task.lifetime_value, task.lifetime_unit)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusChip
                              label={task.recursive ? 'Yes' : 'No'}
                              status={task.recursive ? 'active' : 'disabled'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <StatusChip
                              label={getSnapshotStatus(task) === 'active' ? 'Enabled' : 'Disabled'}
                              status={getSnapshotStatus(task)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Run Snapshot Now">
                              <IconButton 
                                onClick={() => runTask('snapshot', task.id)}
                                size="small"
                                sx={{ 
                                  color: '#00d4ff',
                                  '&:hover': { background: 'rgba(0, 212, 255, 0.1)' }
                                }}
                              >
                                <PlayCircle fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </StyledTableContainer>
            )}
          </Box>

          {/* Cloud Sync Tasks Section */}
          <Box>
            <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CloudSync sx={{ color: '#00ff88' }} />
              Cloud Sync Tasks
            </Typography>
            {cloudSyncTasks.length === 0 ? (
              <Box sx={{
                textAlign: 'center',
                py: 4,
                color: 'rgba(255, 255, 255, 0.7)'
              }}>
                <Typography variant="body1">
                  {loading ? 'Loading cloud sync tasks...' : 'No cloud sync tasks found'}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {cloudSyncTasks.map((task) => (
                  <Grid item xs={12} md={6} lg={4} key={task.id}>
                    <StyledCard>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Cloud sx={{ color: '#00d4ff', fontSize: 28 }} />
                            <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, fontSize: '1rem' }}>
                              {task.description || `Task ${task.id}`}
                            </Typography>
                          </Box>
                          <StatusChip
                            label={getCloudSyncStatus(task)}
                            status={getCloudSyncStatus(task)}
                            size="small"
                          />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <SecondaryText sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                            Direction
                          </SecondaryText>
                          <BodyText sx={{ fontSize: '0.875rem' }}>
                            {task.direction === 'PUSH' ? 'Push to Cloud' : 'Pull from Cloud'}
                          </BodyText>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <SecondaryText sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                            Path
                          </SecondaryText>
                          <BodyText sx={{ fontSize: '0.875rem', wordBreak: 'break-all' }}>
                            {task.path}
                          </BodyText>
                        </Box>

                        {task.credentials && (
                          <Box sx={{ mb: 2 }}>
                            <SecondaryText sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                              Provider
                            </SecondaryText>
                            <BodyText sx={{ fontSize: '0.875rem' }}>
                              {task.credentials.name || task.credentials.provider || 'Unknown'}
                            </BodyText>
                          </Box>
                        )}

                        {task.job && (
                          <>
                            <Box sx={{ mb: 2 }}>
                              <SecondaryText sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                                Last Run
                              </SecondaryText>
                              <BodyText sx={{ fontSize: '0.875rem' }}>
                                {formatDate(task.job.time_finished?.$date)}
                              </BodyText>
                            </Box>

                            {task.job.progress && (
                              <Box sx={{ mb: 2 }}>
                                <SecondaryText sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                                  Progress
                                </SecondaryText>
                                <BodyText sx={{ fontSize: '0.875rem' }}>
                                  {task.job.progress.percent || 0}%
                                  {task.job.progress.description && (
                                    <span style={{ display: 'block', fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
                                      {task.job.progress.description}
                                    </span>
                                  )}
                                </BodyText>
                              </Box>
                            )}

                            {task.job.error && (
                              <Box sx={{ mb: 2, p: 1, backgroundColor: 'rgba(255, 68, 68, 0.1)', borderRadius: '8px' }}>
                                <SecondaryText sx={{ fontSize: '0.75rem', color: '#ff8888' }}>
                                  Error: {task.job.error}
                                </SecondaryText>
                              </Box>
                            )}
                          </>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SecondaryText sx={{ fontSize: '0.75rem' }}>
                              ID: {task.id}
                            </SecondaryText>
                            <StatusChip
                              label={task.enabled ? 'Enabled' : 'Disabled'}
                              status={task.enabled ? 'active' : 'disabled'}
                              size="small"
                            />
                          </Box>
                          <Tooltip title="Run Cloud Sync Now">
                            <IconButton 
                              onClick={() => runTask('cloudsync', task.id)}
                              size="small"
                              disabled={task.job?.state === 'RUNNING'}
                              sx={{ 
                                color: '#00ff88',
                                '&:hover': { background: 'rgba(0, 255, 136, 0.1)' },
                                '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.2)' }
                              }}
                            >
                              <PlayCircle />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Box>
      )}

      {/* Raspberry Pi Backup Content */}
      {(!onlyTrueNAS && (activeTab === 1 || onlyRpi)) && (
        <Box>
          {!rpiBackupScheduled ? (
            <Box sx={{
              textAlign: 'center',
              py: 6,
              color: 'rgba(255, 255, 255, 0.7)'
            }}>
              <Typography variant="body1">
                {loading ? 'Loading Raspberry Pi backup information...' : 'No Raspberry Pi backup information found'}
              </Typography>
            </Box>
          ) : (
            <>
              {/* Scheduled Backup Info */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <StyledCard>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <img src="/raspberry-pi.png" alt="RPi" style={{ width: 28, height: 28 }} />
                        <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, fontSize: '1rem' }}>
                          Backup Schedule
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <SecondaryText sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                          Status
                        </SecondaryText>
                        <StatusChip
                          label={rpiBackupScheduled.enabled ? 'Enabled' : 'Disabled'}
                          status={rpiBackupScheduled.enabled ? 'active' : 'disabled'}
                          size="small"
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <SecondaryText sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                          Interval
                        </SecondaryText>
                        <BodyText sx={{ fontSize: '0.875rem' }}>
                          Every {rpiBackupScheduled.interval_hours} hours
                        </BodyText>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <SecondaryText sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                          Max Backups to Keep
                        </SecondaryText>
                        <BodyText sx={{ fontSize: '0.875rem' }}>
                          {rpiBackupScheduled.max_backups_to_keep}
                        </BodyText>
                      </Box>
                    </CardContent>
                  </StyledCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledCard>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <AccessTime sx={{ color: '#00d4ff', fontSize: 28 }} />
                        <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, fontSize: '1rem' }}>
                          Backup Times
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <SecondaryText sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                          Last Backup
                        </SecondaryText>
                        <BodyText sx={{ fontSize: '0.875rem' }}>
                          {rpiBackupScheduled.last_backup_time ? formatDate(new Date(rpiBackupScheduled.last_backup_time * 1000).toISOString()) : 'Never'}
                        </BodyText>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <SecondaryText sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                          Next Backup
                        </SecondaryText>
                        <BodyText sx={{ fontSize: '0.875rem' }}>
                          {rpiBackupScheduled.next_backup_time ? formatDate(new Date(rpiBackupScheduled.next_backup_time * 1000).toISOString()) : 'Not scheduled'}
                        </BodyText>
                      </Box>
                    </CardContent>
                  </StyledCard>
                </Grid>
              </Grid>

              {/* Backup List */}
              <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, mb: 2 }}>
                Backup History
              </Typography>
              {rpiBackupList.length === 0 ? (
                <Box sx={{
                  textAlign: 'center',
                  py: 4,
                  color: 'rgba(255, 255, 255, 0.7)'
                }}>
                  <Typography variant="body1">
                    No backups found
                  </Typography>
                </Box>
              ) : (
                <StyledTableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Filename</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Size</TableCell>
                        <TableCell>Path</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rpiBackupList.map((backup, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <BackupIcon sx={{ color: '#00d4ff', fontSize: 20 }} />
                              <Typography variant="body2" sx={{ color: '#ffffff' }}>
                                {backup.filename}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                              {backup.created_readable || formatDate(backup.created_iso)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                              {backup.size_gb ? `${backup.size_gb.toFixed(2)} GB` : formatBytes(backup.size_bytes)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                              {backup.path}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </StyledTableContainer>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DataProtection;
